/**
 * Cart Monitor
 * Monitors cart changes and expires rental or resale holds for removed items
 */

import { getApiUrl } from './api-config.js';
import { LOG_PREFIXES } from './api-config.js';
import { expireRentalHold } from './rental-api.js';
import { expireResaleHold } from './resale-ops.js';

(function () {
  let previousCartItems = new Map();

  /**
   * Fetches current cart state
   * @returns {Promise<Map<string, any>>} Map of cart keys to items with hold UIDs
   */
  async function fetchCurrentCartItems() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return new Map();
      
      const cart = await response.json();
      const itemsMap = new Map();
      
      cart.items?.forEach(/** @param {any} item */ (item) => {
        const rentalHoldUid = item.properties?._rental_hold_uid;
        const resaleHoldUid = item.properties?._resale_hold_uid;
        
        if (rentalHoldUid) {
          itemsMap.set(item.key, {
            key: item.key,
            holdUid: rentalHoldUid,
            type: 'rental',
            quantity: item.quantity
          });
        }
        
        if (resaleHoldUid) {
          itemsMap.set(item.key, {
            key: item.key,
            holdUid: resaleHoldUid,
            type: 'resale',
            quantity: item.quantity
          });
        }
      });
      
      return itemsMap;
    } catch (err) {
      console.error(`${LOG_PREFIXES.API} Failed to fetch cart:`, err);
      return new Map();
    }
  }

  /**
   * Handles cart updates and expires holds for removed items
   */
  async function handleCartUpdate() {
    const currentCartItems = await fetchCurrentCartItems();
    const apiUrl = getApiUrl('');
    
    // Check for removed items
    for (const [key, prevItem] of previousCartItems) {
      if (!currentCartItems.has(key)) {
        if (prevItem.type === 'rental') {
          console.log(`${LOG_PREFIXES.RENTAL} Item removed from cart, expiring hold`);
          await expireRentalHold(prevItem.holdUid, apiUrl);
        } else if (prevItem.type === 'resale') {
          console.log(`${LOG_PREFIXES.RESALE} Item removed from cart, expiring hold`);
          await expireResaleHold(prevItem.holdUid, apiUrl);
        }
      }
    }
    
    previousCartItems = currentCartItems;
  }

  // Initialize cart state
  fetchCurrentCartItems().then(items => {
    previousCartItems = items;
  });

  // Listen for cart updates
  document.addEventListener('cart:update', handleCartUpdate);
  document.addEventListener('theme:cart:change', handleCartUpdate);

})();

